import { Component, ViewEncapsulation, ViewChild, HostListener, OnDestroy } from '@angular/core';
import { CdkScrollable } from '@angular/cdk/overlay';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Subscription, combineLatest, take } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';

import { TranslationService } from '../../../../../../shared/services/translation.service';
import { pageTransitions } from '../../../../../../shared/animations/page-transitions';
import {
	StepClickedEvent,
	StepStatus,
	StepperComponent
} from '../../../../../../shared/components/stepper/stepper.component';
import { LocationService } from '../../../../../../shared/services/location.service';
import { AddUserService } from '../../../../../services/add-user.service';
import {
	AddUserInfo,
	AddUserPageContent, PersonRoleAndServiceRequest, ServiceRequest
} from '../../../../../../shared/models/add-user-info';
import { User } from '../../../../../../shared/models/user';
import { UserService } from '../../../../../../shared/services/user.service';
import { AfterViewInitDirective } from '../../../../../../shared/directives/after-view-init.directive';
import { LoadingSpinnerComponent } from '../../../../../../shared/components/utils/loading-spinner.component';
import { PageHeaderComponent } from '../../../../../../shared/components/page-header/page-header.component';
import { ScrollerDirective } from '../../../../../../shared/directives/scroller.directive';

@Component({
	selector: 'copo-add-user-to-organization',
	templateUrl: './add-user-to-organization.component.html',
	styleUrl: './add-user-to-organization.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		PageHeaderComponent,
		NgIf,

		MatButtonModule,
		TranslateModule,
		StepperComponent,
		LoadingSpinnerComponent,
		RouterOutlet,
		AfterViewInitDirective,
	],
})
export class AddUserToOrganizationComponent implements OnDestroy {
	@ViewChild('scroller', { read: CdkScrollable }) scrollBar?: CdkScrollable;
	pageContent: AddUserPageContent | undefined;
	isLoading = false;
	hideNextBtn = false;
	pageTitle: string;
	firstName = '';
	lastName = '';
	administrator = false;
	email = '';
	userType = '';
	isAnyOneSelected?: boolean;
	sub?: Subscription;
	addUserSub?: Subscription;
	addUserSubscription?: Subscription;
	orgId = '';
	userId = '';
	servicesToAdd: string[] = [];
	isNewUser = false;
	user?: User;
	readonly steps;

	private readonly pageTitleTranslationCode = 'add-user.user-creation';
	private readonly routeSub: Subscription = Subscription.EMPTY;

	constructor(
		private readonly translationService: TranslationService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly userService: UserService,
		private readonly addUserService: AddUserService,
		private readonly locationService: LocationService,

		private readonly snackBar: MatSnackBar,
	) {
		this.steps = [
			{
				label: this.translationService.getTR('add-user.user-informations'),
				status: StepStatus.CURRENT,
			},
			{
				label: this.translationService.getTR('add-user.matching-users'),
				status: StepStatus.DISABLED,
			},
			{
				label: this.translationService.getTR('add-user.services-management'),
				status: StepStatus.DISABLED,
			},
			{
				label: this.translationService.getTR('add-user.confirmation'),
				status: StepStatus.DISABLED,
			}
		];
		this.user = this.userService.user;
		if (!this.route.parent?.parent?.parent) {
			throw new Error('wrong url');
		}
		this.routeSub = this.route.parent.paramMap.subscribe(params => {
			const idString = params.get('id');
			if (idString === null) {
				return;
			}
			this.orgId = idString;
			this.addUserService.updateOrgId(this.orgId);
		});
		this.pageTitle = this.translationService.getTR(this.pageTitleTranslationCode);
		this.sub = this.addUserService.isNewUserCreation.subscribe(newUser => {
			this.isNewUser = newUser;
		});
		this.sub = this.addUserService.userId.subscribe(userID => {
			this.userId = userID;
		});
		this.sub = this.addUserService.firstName.subscribe(firstName => {
			this.firstName = firstName;
		});
		this.sub = this.addUserService.lastName.subscribe(lastName => {
			this.lastName = lastName;
		});
		this.sub = this.addUserService.email.subscribe(email => {
			this.email = email;
		});
		this.sub = this.addUserService.userType.subscribe(userType => {
			this.userType = userType;
		});
		this.sub = this.addUserService.administrator.subscribe(administrator => {
			this.administrator = administrator;
		});
		this.sub = this.addUserService.isAnyOneSelected
			.subscribe(isAnyOneSelected => {
				this.isAnyOneSelected = isAnyOneSelected;
			});
		this.clearAll();
	}

	@HostListener('window:beforeunload') goToPage() {
		if (this.addUserService.getCurrentRoute().endsWith('/users/add/stepConfirmation')) {
			this.locationService.go(-3, `/user/organization/${ this.orgId }/users/add/stepUserInfo`);
		} else if (this.addUserService.getCurrentRoute().endsWith('/users/add/stepServiceManagement')) {
			this.locationService.go(-2, `/user/organization/${ this.orgId }/users/add/stepUserInfo`);
		} else if (this.addUserService.getCurrentRoute().endsWith('/users/add/stepMatchingUsers')) {
			this.locationService.go(-1, `/user/organization/${ this.orgId }/users/add/stepUserInfo`);
		}
		this.clearAll();
	}

	goToUserList() {
		this.clearAll();
		this.locationService.go(-1, `/user/organization/${ this.orgId }/users`);
	}

	goBack() {
		this.locationService.go(-1, `/user/organization/${ this.orgId }/users`);
		this.clearAll();
	}

	clearAll() {
		this.addUserService.updateFirstName('');
		this.addUserService.updateLastName('');
		this.addUserService.updateEmail('');
		this.addUserService.updateServices([]);
		this.addUserService.updateAdmin(false);
		this.addUserService.updateIsAnyOneChoiceSelected(false);
		this.addUserService.updateNodeSelected(undefined);
		this.addUserService.setSelectedUser(undefined);
	}

	async onStepClicked(event: StepClickedEvent) {
		this.steps[event.stepIndex].status = StepStatus.CURRENT;
		switch (event.stepIndex) {
		case 3:
			this.steps[event.stepIndex - 1].status = StepStatus.ENABLED;
			this.steps[event.stepIndex - 2].status = StepStatus.ENABLED;
			this.steps[event.stepIndex - 3].status = StepStatus.ENABLED;
			await this.router.navigate(
				[`/user/organization/${ this.orgId }/users/add/stepConfirmation`],
				{ relativeTo: this.route }
			);
			this.hideNextBtn = true;
			break;
		case 2:
			this.steps[event.stepIndex - 1].status = StepStatus.ENABLED;
			this.steps[event.stepIndex - 2].status = StepStatus.ENABLED;
			this.steps[event.stepIndex + 1].status = StepStatus.DISABLED;
			await this.router.navigate(
				[`/user/organization/${ this.orgId }/users/add/stepServiceManagement`],
				{ relativeTo: this.route }
			);
			this.hideNextBtn = false;
			break;
		case 1:
			this.steps[event.stepIndex + 1].status = StepStatus.DISABLED;
			this.steps[event.stepIndex - 1].status = StepStatus.ENABLED;
			this.steps[event.stepIndex + 2].status = StepStatus.DISABLED;
			await this.router.navigate(
				[`/user/organization/${ this.orgId }/users/add/stepMatchingUsers`],
				{ relativeTo: this.route }
			);
			this.hideNextBtn = false;
			break;
		default:
			this.steps[event.stepIndex + 1].status = StepStatus.DISABLED;
			this.steps[event.stepIndex + 2].status = StepStatus.DISABLED;
			this.steps[event.stepIndex + 3].status = StepStatus.DISABLED;
			await this.router.navigate(
				[`/user/organization/${ this.orgId }/users/add/stepUserInfo`],
				{ relativeTo: this.route }
			);
			this.hideNextBtn = false;
			break;
		}
	}

	onActivate(pageContent: AddUserPageContent) {
		this.pageContent = pageContent;
	}

	async goToNext() {
		if (this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 0) {
			this.pageContent?.checkErrors();
			if (this.firstName && this.lastName && this.email && this.pageContent?.checkErrors()) {
				this.steps[1].status = StepStatus.CURRENT;
				this.steps[2].status = StepStatus.DISABLED;
				this.steps[3].status = StepStatus.DISABLED;
				this.steps[0].status = StepStatus.ENABLED;
				await this.router.navigate(
					[`/user/organization/${ this.orgId }/users/add/stepMatchingUsers`],
					{ relativeTo: this.route }
				);
			}
		} else if (this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 1
		) {
			this.pageContent?.checkErrors();
			if (this.isAnyOneSelected && this.pageContent?.checkErrors()) {
				this.steps[1].status = StepStatus.ENABLED;
				this.steps[2].status = StepStatus.CURRENT;
				this.steps[0].status = StepStatus.ENABLED;
				this.steps[3].status = StepStatus.DISABLED;
				await this.router.navigate(
					[`/user/organization/${ this.orgId }/users/add/stepServiceManagement`],
					{ relativeTo: this.route }
				);
			}
		} else if (this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 2
		) {
			this.steps[1].status = StepStatus.ENABLED;
			this.steps[2].status = StepStatus.ENABLED;
			this.steps[0].status = StepStatus.ENABLED;
			this.steps[3].status = StepStatus.CURRENT;
			await this.router.navigate(
				[`/user/organization/${ this.orgId }/users/add/stepConfirmation`],
				{ relativeTo: this.route }
			);
			this.hideNextBtn = true;
		}
	}

	isButtonSubmitVisible() {
		return !this.isLoading && this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 3;
	}

	addUser() {
		this.addUserSubscription = combineLatest([
			this.addUserService.services.pipe(take(1)),
			this.addUserService.administrator.pipe(take(1))
		]).subscribe(([services, isAdmin]) => {
			for (const ser of services) {
				this.servicesToAdd.push(ser.id);
			}

			if (isAdmin && !this.servicesToAdd.includes('PORUM')) {
				this.servicesToAdd.push('PORUM');
			}
		});
		const serviceRequests: ServiceRequest[] = [];
		for (const service of this.servicesToAdd) {
			const serviceRequest: ServiceRequest = {
				id: service
			};
			serviceRequests.push(serviceRequest);
		}
		let snackbarAddUser: string;
		let displaySnackBar = true;
		if (this.isNewUser) {
			this.createNewUserAndAddToOrg(serviceRequests);
			// add user button will be present only if the user is delegating helpdesk or admin
			if (!this.user?.isDelegating() && this.userType === 'subscription') {
				displaySnackBar = false;
			}
			snackbarAddUser = !this.user?.isDelegating()
				? 'snackbar.add.user.validation.mail-external'
				: 'snackbar.add.user.validation.mail-internal';
		} else {
			this.addExistingUserToOrg(serviceRequests);
			// add user button will be present only if the user is delegating helpdesk or admin
			snackbarAddUser = !this.user?.isDelegating()
				? 'snackbar.add.existing.user.validation.mail-external'
				: 'snackbar.add.user.validation.mail-internal';
		}
		if (displaySnackBar) {
			this.snackBar.open(
				this.translationService.getTR(snackbarAddUser),
				undefined,
				{
					duration: 5000,
					panelClass: ['custom-snackbar']
				},
			);
		}
	}

	createNewUserAndAddToOrg(serviceRequests: ServiceRequest[]) {
		const requestBody: AddUserInfo = {
			firstname: this.firstName,
			lastname: this.lastName,
			email: this.email,
			services: serviceRequests,
			admin: this.administrator,
			organizationId: Number(this.orgId),
			userType: this.userType
		};
		this.addUserSub = this.addUserService.addUser(requestBody).subscribe({
			next: () => {
				this.goToUserList();
			},
			error: (error: unknown) => {
				if (error instanceof HttpErrorResponse && this.user && !this.user.readOnly) {
					this.displayErrorMessage((<{ message: string }> error.error).message);
				}
			}
		});
	}

	addExistingUserToOrg(serviceRequests: ServiceRequest[]) {
		const requestBody: PersonRoleAndServiceRequest = {
			role: this.administrator ? 'DSU' : 'user',
			services: serviceRequests
		};

		this.addUserSub = this.addUserService
			.addUserToOrg(requestBody, Number(this.orgId), Number(this.userId)).subscribe({
				next: () => {
					this.goToUserList();
				},
				error: (error: unknown) => {
					if (error instanceof HttpErrorResponse) {
						this.displayErrorMessage((<{ message: string }>error.error).message);
					}
				}
			});
	}

	ngOnDestroy() {
		this.routeSub.unsubscribe();
		if (this.sub !== undefined) {
			this.sub.unsubscribe();
		}
		if (this.addUserSub !== undefined) {
			this.addUserSub.unsubscribe();
		}
		if (this.addUserSubscription !== undefined) {
			this.addUserSubscription.unsubscribe();
		}
	}

	displayErrorMessage(errMsg: string) {
		this.snackBar.open(errMsg, undefined, {
			duration: 3000,
		});
		this.clearAll();
		this.goToUserList();
	}
}
